import React, { useMemo, useState } from 'react';
import {
  AnswerInput,
  HelperText,
  ProgramEnumResponse,
  useProgram,
} from 'shared/providers/ProgramProvider';
import { BENEFICIARY_TERM_QUESTION_ID } from 'shared/providers/ProgramProvider/utils';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { OtherTextField } from 'npo/pages/ReportBuilder/Components/ImpactSummary/SectionService';
import SelectorField from './SelectorField';
import { useBlockedSignal } from './useBlockedSignal';

const OTHER_TEXT = 'Other';

interface IntermediariesFieldProps {
  sectionKey: string | null | undefined;
  questionId: number;
  enumOptions: ProgramEnumResponse[];
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
  onBlockedChange?: (blocked: boolean) => void;
}

// Q40022 intermediaries — only reached when Q40006 was answered
// "indirectly". Mirrors ReportBuilder's SectionServiceRender handling of
// the BENEFICIARIES type: radio over the enum options (beneficiary-term
// placeholders resolved) with an "Other" free-text entry. The chosen or
// typed term is persisted as plain `text`.
const IntermediariesField: React.FC<IntermediariesFieldProps> = ({
  sectionKey,
  questionId,
  enumOptions,
  value,
  onChange,
  disabled,
  onBlockedChange,
}) => {
  const { answers } = useProgram();
  const beneficiaryTerm =
    answers[BENEFICIARY_TERM_QUESTION_ID]?.text || 'beneficiaries';

  const options = useMemo(
    () =>
      enumOptions.map(opt => ({
        dbId: opt.dbId,
        label: opt.response
          .replace(/<Primary_beneficiary>/g, beneficiaryTerm)
          .replace(/<Primary_beneficiary_or_Intermediary>/g, beneficiaryTerm),
      })),
    [enumOptions, beneficiaryTerm],
  );

  const current = value.text ?? '';
  const matchesOption = options.some(o => o.label === current);
  // "Other" mode is on when the stored text is a custom term, or the user
  // picked "Other" and hasn't typed anything yet.
  const [otherMode, setOtherMode] = useState<boolean>(
    Boolean(current) && !matchesOption,
  );
  const isOther = otherMode || (Boolean(current) && !matchesOption);
  const customText = isOther && current !== OTHER_TEXT ? current : '';

  useBlockedSignal(isOther && customText.trim().length === 0, onBlockedChange);

  const commit = (text: string, answerId: number | null) =>
    onChange({ ...value, text: text || null, questionText: text || null, answerId });

  return (
    <>
      <HelperText sectionKey={sectionKey} questionId={questionId} />
      <SelectorField>
        <SelectorOptions
          options={options}
          variant="radio"
          margin="0"
          maxWidth="100%"
          disabled={disabled}
          getLabel={opt => opt.label}
          getSelected={opt =>
            opt.label === OTHER_TEXT ? isOther : !isOther && current === opt.label
          }
          onClick={opt => {
            if (disabled) return;
            if (opt.label === OTHER_TEXT) {
              setOtherMode(true);
              commit(OTHER_TEXT, null);
              return;
            }
            setOtherMode(false);
            commit(opt.label, opt.dbId);
          }}
        />
        {isOther && (
          <OtherTextField
            value={customText}
            placeholder="Enter your own term here"
            disabled={disabled}
            autoFocus={true}
            onChange={e => commit(e.target.value || OTHER_TEXT, null)}
          />
        )}
      </SelectorField>
    </>
  );
};

export default IntermediariesField;
